import styled from 'styled-components'
import { Column, Row } from '@shipt/design-system-layouts'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { type Product } from '@/features/shop/services/Product/types'
import { getProductDetails } from '@/features/shared/utils/getProductDetails'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { ProductVariationInfo } from '@/features/shop/components/ProductCard/colors/ProductVariationInfo'
import { ProductCardPromotion } from '@/features/shop/components/ProductCard/price/ProductCardPromotion'
import { useIsVisitorOrZipOnly } from '@/features/account/services/User/hooks'
import { ProductCardPrice } from '@/features/shop/components/ProductCard/price/ProductCardPrice'
import { EBTEligible } from '@/features/purchase/components/EBT/EBTEligible'
import { Body } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'

type Props = {
  product: Product
  hasBackground?: boolean
}

export const LandscapeProductWrapper = ({
  product,
  hasBackground,
  children,
  className,
}: Props & { children: React.ReactNode; className?: string }) => (
  <Wrapper className={className} spacing={{ base: 'lg', md: 'xl' }}>
    <ProductImage height={104} width={104} src={product.image.url} alt={product.name} />
    <ProductInfoContainer {...(hasBackground && { justify: 'center' })}>
      <SkeletonConsumer height={60} width={136}>
        <ProductName size="md">{product.name}</ProductName>
      </SkeletonConsumer>
      {children}
    </ProductInfoContainer>
  </Wrapper>
)

export const LandscapeProduct = ({ product, hasBackground }: Props) => {
  const isVisitor = useIsVisitorOrZipOnly()
  const { onSale } = product
  const { subtext, hasPromotion } = getProductDetails(product)

  return (
    <LandscapeProductWrapper product={product} hasBackground={hasBackground}>
      <SkeletonConsumer height={20} width={40}>
        <Row spacing="sm" align="baseline">
          <ProductCardPrice product={product} />
          <SubText data-on-sale={onSale} size="sm" variant="secondary">
            {subtext}
          </SubText>
        </Row>
        <EBTEligible isSnapEbtEligible={product.is_snap_ebt_eligible} />
        {!!product.featured && (
          <Body size="sm" variant="secondary">
            Sponsored
          </Body>
        )}
        {!isVisitor && (
          <PromotionsAndVariations data-has-coupon={Boolean(product.couponId)}>
            {product.parent ? (
              <ProductVariationInfo variationParent={product.parent} />
            ) : (
              hasPromotion && <ProductCardPromotion product={product} />
            )}
          </PromotionsAndVariations>
        )}
      </SkeletonConsumer>
    </LandscapeProductWrapper>
  )
}

const Wrapper = styled(Row)`
  width: 100%;
  min-width: 0;
  max-width: 100%;
`

const ProductInfoContainer = styled(Column)`
  flex: 1 1 auto;
  min-width: 0;
`

const ProductImage = styled(Image)`
  object-fit: contain;
  flex-shrink: 0;

  @media ${screenSizes.max_mobile} {
    width: 88px;
    height: 88px;
  }
`

const ProductName = styled(Body)`
  /* stylelint-disable-next-line value-no-vendor-prefix -- -webkit-line-clamp used here is only compatible with -webkit-box so we need to keep using it */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const SubText = styled(Body)<{
  'data-on-sale': boolean
}>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &[data-on-sale='true'] {
    width: 50px;
    display: block;
  }
`
const PromotionsAndVariations = styled.div<{
  'data-has-coupon': boolean
}>`
  /* With coupon - style button */
  &[data-has-coupon='true'] {
    button {
      width: 90%;
    }
  }
`
